import { Box, Divider, Tooltip } from "@mui/material";
import { capitalize } from "lodash";
import Flex from "../../../../../components/Flex";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import StatusChip from "../../../../../components/StatusChip";
import Text from "../../../../../components/Text";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useGetEobById } from "../../../../../queries/billings/eob/use-get-eob-by-id";
import { formatDate } from "../../../../../utils/date-time-utility";
import {
  getTrimmedUuid,
  replaceUnderScoreAddSpace,
} from "../../../../../utils/utils";
import { EOB_STATUS_VARIANT, getEobRemainingPayment } from "./constants";

interface ViewEobDetailsProps {
  disclosureHost: DisclosureHost;
  onPreviewDocument?: (uuid: string) => void;
}

interface EobDetailDto {
  uuid?: string;
  insurancePayer?: { payerId?: string; payerName?: string };
  eobNumber?: string;
  paymentMethod?: string;
  checkEftNumber?: string;
  totalPaymentAmount?: string | number;
  statementDate?: string;
  document?: string;
  documentName?: string;
  documentFormat?: string;
  remainingPayment?: string | number | null;
  status?: string | null;
  postedClaims?: Record<string, string> | null;
  created?: string;
  uploadedBy?: string;
}

const formatCurrency = (amount: number | string | null | undefined) => {
  const n = Number(amount ?? 0);
  return `$${n.toFixed(2)}`;
};

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Flex flexDirection="column" gap={0.5} sx={{ minWidth: 0 }}>
    <Text variant="caption" sx={{ color: "text.secondary" }}>
      {label}
    </Text>
    <Box sx={{ wordBreak: "break-word" }}>{children}</Box>
  </Flex>
);

const ViewEobDetails = ({
  disclosureHost,
  onPreviewDocument,
}: ViewEobDetailsProps) => {
  const eobUuid = disclosureHost.opened
    ? (disclosureHost.data as string | undefined)
    : undefined;

  const { data: eobDetail, isLoading } = useGetEobById(eobUuid);
  const eob = (eobDetail as any)?.data as EobDetailDto | undefined;

  const status = eob?.status?.toUpperCase() || "PENDING";
  const postedClaimUuids = Object.values(eob?.postedClaims ?? {});

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      onClose={disclosureHost.close}
      title="EOB Details"
      w={520}
    >
      {isLoading && (
        <Box sx={{ p: 3 }}>
          <Text>Loading...</Text>
        </Box>
      )}

      {!isLoading && eob && (
        <Box sx={{ p: 3 }}>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            sx={{ flexWrap: "wrap", gap: 1 }}
          >
            <Text variant="h6" fontWeight={600}>
              {eob?.eobNumber || "-"}
            </Text>
            <StatusChip
              label={capitalize(status)}
              variant={EOB_STATUS_VARIANT[status] ?? "default"}
            />
          </Flex>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              rowGap: 2,
              columnGap: 2,
            }}
          >
            <DetailRow label="Payer Name">
              <Text>{eob?.insurancePayer?.payerName || "-"}</Text>
            </DetailRow>

            <DetailRow label="EOB #">
              <Text>{eob?.eobNumber || "-"}</Text>
            </DetailRow>

            <DetailRow label="Doc. Name">
              {eob?.document && eob?.documentName ? (
                <Tooltip title={eob.documentName} arrow placement="top">
                  <span>
                    <Text
                      sx={{
                        color: "#2C57B3",
                        cursor: "pointer",
                        fontWeight: 500,
                        textDecoration: "underline",
                      }}
                      onClick={() => eob?.uuid && onPreviewDocument?.(eob.uuid)}
                    >
                      {eob.documentName}
                    </Text>
                  </span>
                </Tooltip>
              ) : (
                <Text>-</Text>
              )}
            </DetailRow>

            <DetailRow label="Payment Method">
              <Text>
                {replaceUnderScoreAddSpace(eob?.paymentMethod ?? "") || "-"}
              </Text>
            </DetailRow>

            <DetailRow label="Cheque/EFT #">
              <Text>{eob?.checkEftNumber || "-"}</Text>
            </DetailRow>

            <DetailRow label="Payment Amount">
              <Text>{formatCurrency(eob?.totalPaymentAmount)}</Text>
            </DetailRow>

            <DetailRow label="Statement Date">
              <Text>
                {eob?.statementDate ? formatDate(eob.statementDate) : "-"}
              </Text>
            </DetailRow>

            <DetailRow label="Remaining Payment">
              <Text>{formatCurrency(getEobRemainingPayment(eob ?? {}))}</Text>
            </DetailRow>

            <DetailRow label="Uploaded By">
              <Text>{eob?.uploadedBy || "-"}</Text>
            </DetailRow>

            <DetailRow label="Upload Date">
              <Text>{eob?.created ? formatDate(eob.created) : "-"}</Text>
            </DetailRow>

            <Box sx={{ gridColumn: "1 / -1" }}>
              <DetailRow label="Posted Claims">
                {postedClaimUuids.length === 0 ? (
                  <Text>-</Text>
                ) : (
                  <Flex sx={{ flexWrap: "wrap", gap: 0.5 }}>
                    {postedClaimUuids.map((claimUuid) => (
                      <Box
                        key={claimUuid}
                        sx={{
                          px: 1,
                          py: 0.25,
                          border: "1px solid",
                          borderColor: "grey.300",
                          borderRadius: 1,
                          bgcolor: "grey.50",
                        }}
                      >
                        <Text variant="body2">{getTrimmedUuid(claimUuid)}</Text>
                      </Box>
                    ))}
                  </Flex>
                )}
              </DetailRow>
            </Box>
          </Box>
        </Box>
      )}

      {!isLoading && !eob && (
        <Box sx={{ p: 3 }}>
          <Text>No EOB details available.</Text>
        </Box>
      )}
    </SideSheetDrawer>
  );
};

export default ViewEobDetails;
